import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import type { ComponentFailurePlaceholderBlock } from '@/@types/content';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComponentFailureVariant,
  DefaultComponentContent
} from '@/components/blocks/ErrorFallback/util';
import { useGetGlobalElementQuery } from '@/redux/api/contentDeliveryAPI';
import { simpleHash } from '@/utils/helpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import Shimmer from '@/components/ui/Shimmer';
import ComponentFallback from './ComponentFallback';
import { screens } from '@/constants/tailwind';

export type ComponentFailurePlaceholderBlockProps = ComponentFailurePlaceholderBlock & {
  sectionClassNames?: string;
  containerClassNames?: string;
  buttonClassNames?: string;
  isRetrying?: boolean;
  /** Ignore if page reload required */
  onRetryButtonClick?: () => void;
};

/**
 * Renders a placeholder block to display when a component fails to load or encounters an error.
 *
 * This block can show a heading, body text, and a retry button, with customizable content and styles.
 * It fetches fallback content from a global element query and adapts its layout responsively.
 *
 * @param headingText - Optional custom heading text to display.
 * @param bodyText - Optional custom body text to display.
 * @param buttonText - Optional custom label for the retry button.
 * @param onRetryButtonClick - Optional callback to invoke when the retry button is clicked.
 * @param sectionClassNames - Optional additional class names for the section element.
 * @param buttonClassNames - Optional additional class names for the retry button.
 * @param containerClassNames - Optional additional class names for the container element.
 * @param isRetrying - Indicates whether a retry operation is in progress.
 * @param variant - Optional variant to control the block's layout style.
 *
 * @returns A React component that displays an error fallback UI with loading shimmer and retry functionality.
 */
const ComponentFailurePlaceholderBlock = ({
  headingText,
  bodyText,
  buttonText,
  onRetryButtonClick,
  sectionClassNames,
  buttonClassNames,
  containerClassNames,
  isRetrying,
  variant
}: ComponentFailurePlaceholderBlockProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(!!isRetrying);
  const sectionRef = useRef<HTMLElement>(null);
  const userId = useSelector((state: RootState) => state.auth?.name || 'ANON');
  const navigate = useNavigate();
  const {
    data: content,
    isLoading: isGlobalLoading,
    isFetching
  } = useGetGlobalElementQuery({
    name: 'component-fallback',
    userId: simpleHash(userId),
    query: { select: ['bodyText', 'headingText', 'buttonText', 'variant'] }
  });

  const {
    bodyText: fallbackBodyText,
    headingText: fallbackHeadingText,
    buttonText: fallbackButtonText,
    variant: fallbackVariant
  } = DefaultComponentContent;

  const screenSizeMd = useMediaQuery(ScreenSizeQueries.md);
  const screenSizeBelowMd = useMediaQuery(ScreenSizeQueries.belowMd);
  const screenSizelg = useMediaQuery(ScreenSizeQueries.lg);
  const screenSizeSm = useMediaQuery(ScreenSizeQueries.smOnly);
  const screenSizeXsOnly = useMediaQuery(ScreenSizeQueries.xsOnly);

  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(element);

    // Initial width
    setWidth(element.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onButton = useCallback(() => {
    setIsLoading(true);
    if (onRetryButtonClick) onRetryButtonClick();
    else navigate(0);
  }, [navigate, onRetryButtonClick]);

  const isLoadingContent = useMemo(
    () => !!isGlobalLoading || isLoading || isFetching,
    [isFetching, isGlobalLoading, isLoading]
  );

  const body = useMemo(
    () => bodyText ?? (content?.bodyText as string) ?? fallbackBodyText,
    [bodyText, content?.bodyText, fallbackBodyText]
  );

  const heading = useMemo(
    () => headingText ?? (content?.headingText as string) ?? fallbackHeadingText,
    [content?.headingText, fallbackHeadingText, headingText]
  );

  const buttonLabel = useMemo(
    () => buttonText ?? (content?.buttonText as string) ?? fallbackButtonText,
    [buttonText, content?.buttonText, fallbackButtonText]
  );

  const blockVariant = useMemo(
    () => variant ?? content?.variant ?? fallbackVariant,
    [content?.variant, fallbackVariant, variant]
  );

  const sectionClasses = classnames(
    'container module-spacing m-auto flex size-full',
    sectionClassNames
  );

  const containerClasses = classnames(
    `flex size-full flex-col items-start justify-center self-center rounded-sm bg-gray-lightest py-12`,
    {
      'mx-auto px-10': screenSizeMd,
      'px-6': screenSizeBelowMd,
      'justify-between': blockVariant == ComponentFailureVariant.spaceBetween && screenSizeMd
    },
    containerClassNames
  );

  const buttonClasses = classnames(
    'px-8 py-6',
    {
      'mt-auto': blockVariant == ComponentFailureVariant.spaceBetween && screenSizeMd,
      'w-auto': screenSizeSm,
      'w-full':
        ((screenSizeXsOnly || screenSizelg) &&
          blockVariant == ComponentFailureVariant.spaceBetween) ||
        (screenSizeXsOnly && blockVariant != ComponentFailureVariant.spaceBetween)
    },
    buttonClassNames
  );

  const smallScreenWidth = useMemo(() => parseInt(screens.sm.replace('px', '')) - 1, []);
  const loader = useMemo(() => {
    const shimmer1 = classnames('mb-3 h-16 w-full', {
      'w-1/3': width > smallScreenWidth
    });
    const shimmer2 = classnames('mb-6 h-8 w-full', {
      'w-1/2': width > smallScreenWidth
    });
    const shimmer3 = classnames('h-14 w-full', {
      'w-36': width > smallScreenWidth
    });
    return (
      <>
        <div className="flex w-full flex-col items-start justify-start">
          <Shimmer className={shimmer1} />
          <Shimmer className={shimmer2} />
        </div>
        <Shimmer className={shimmer3} />
      </>
    );
  }, [smallScreenWidth, width]);

  return (
    <section className={sectionClasses} ref={sectionRef} data-error-boundary-fallback>
      <div className={containerClasses}>
        {isLoadingContent ? (
          loader
        ) : (
          <ComponentFallback
            body={body}
            heading={heading}
            buttonLabel={buttonLabel}
            buttonClasses={buttonClasses}
            onButtonClick={onButton}
            isRetrying={isRetrying}
          />
        )}
      </div>
    </section>
  );
};

export default ComponentFailurePlaceholderBlock;
